import { useContext, useEffect, useState } from 'react';
import { Context as StoreContext } from '../../providers/StoreContextProvider';
import { Context as StoreUserContext } from '../../providers/StoreUserContextProvider';
import { Context as AuthContext } from '../../providers/AuthContextProvider';
import { Context as HelperContext } from '../../providers/HelperContextProvider';
import { useCartStorageController } from './useCartStorageController';
import { createFetchStoreCart } from './webstoreCheckoutUtilities';

const useCartShippingController = ({
  isAdminCheckout = false,
  pkEcomOrder = null,
  fetchShippingOnAddressChange = false
} = {}) => {
  const { state: authState } = useContext(AuthContext);
  const { state: helperState, setWebstoreShippingAddress } = useContext(
    HelperContext
  );
  const { state, fetchEcomStoreCart, fetchEcomStoreGuestCart } = useContext(
    StoreUserContext
  );
  const { state: adminState, fetchEcomStoreOrderAdminCart } = useContext(
    StoreContext
  );
  const {
    getGuestCartStorage,
    setCartShippingAddress,
    setCartShippingOptions
  } = useCartStorageController({
    isAdminCheckout,
    pkEcomOrder
  });
  const [debouncedState, setDebounceState] = useState({
    key: null,
    loading: false,
    value: null
  });

  const { shippingAddress } = helperState.webstore;

  const { value: ecomStoreCart } = isAdminCheckout
    ? adminState.ecomOrderAdminCart
    : authState.isAuthenticated
    ? state.ecomStoreCart
    : state.ecomStoreGuestCart;

  useEffect(() => {
    if (ecomStoreCart && !shippingAddress) {
      const cart = getGuestCartStorage(ecomStoreCart.fkEcomStore);
      // eslint-disable-next-line no-use-before-define
      onChangeShippingAddress(
        cart.shippingAddress || { country: 'CA', state: 'ON' },
        true
      );
    }
  }, [ecomStoreCart]);

  useEffect(() => {
    if (
      fetchShippingOnAddressChange &&
      shippingAddress?.hasChanged &&
      !shippingAddress.skipDebounce
    ) {
      setDebounceState((c) => ({
        ...c,
        loading: true
      }));
      const handler = setTimeout(() => {
        setDebounceState((c) => ({
          ...c,
          value: shippingAddress
        }));
      }, 800);

      return () => {
        clearTimeout(handler);
      };
    }
    return () => {};
  }, [shippingAddress]);

  useEffect(() => {
    if (
      fetchShippingOnAddressChange &&
      debouncedState.value !== null &&
      ecomStoreCart
    ) {
      if (debouncedState.value.country && debouncedState.value.postalCode) {
        const cart = getGuestCartStorage(ecomStoreCart.fkEcomStore);
        if (
          cart.shippingAddress?.city !== debouncedState.value.city ||
          cart.shippingAddress?.postalCode !==
            debouncedState.value.postalCode ||
          cart.shippingAddress?.country !== debouncedState.value.country ||
          ecomStoreCart.checkoutCart.shippingCostMissing
        ) {
          // eslint-disable-next-line no-use-before-define
          onFetchUpdatedShippingCosts(ecomStoreCart.fkEcomStore, {
            ecomCustomerAddress: debouncedState.value
          });
        }

        setCartShippingAddress(ecomStoreCart.fkEcomStore, debouncedState.value);
      }

      setDebounceState((c) => ({
        ...c,
        loading: false
      }));
    }
  }, [debouncedState.value]);

  const onChangeShippingAddress = (newAddress, skipDebounce = false) => {
    setWebstoreShippingAddress({
      ...shippingAddress,
      ...newAddress,
      skipDebounce,
      hasChanged: true
    });
  };

  const onFetchUpdatedShippingCosts = (
    pkEcomStore,
    { ecomCustomerAddress }
  ) => {
    const fetchStoreCart = createFetchStoreCart({
      isAdminCheckout,
      authState,
      getGuestCartStorage,
      setCartShippingOptions,
      fetchEcomStoreCart,
      fetchEcomStoreGuestCart,
      fetchEcomStoreOrderAdminCart
    });
    fetchStoreCart(
      pkEcomStore,
      {
        ecomCustomerAddress,
        includeShipping: true
      },
      () => {}
    );
  };

  const onShippingCallback = (pkEcomStore, data) => {
    setCartShippingOptions(
      pkEcomStore,
      data.checkoutCart.shippingCosts.map((s) => ({
        ecomExternalConnectionExternalId: s.ecomExternalConnectionExternalId,
        fkEcomExternalConnectionType: s.fkEcomExternalConnectionType,
        value: s.value,
        label: s.label
      }))
    );
  };

  const onChangeShippingCosts = (
    pkEcomStore,
    { ecomCustomerAddress, shippingOptions }
  ) => {
    const fetchStoreCart = createFetchStoreCart({
      isAdminCheckout,
      authState,
      getGuestCartStorage,
      onShippingCallback,
      fetchEcomStoreCart,
      fetchEcomStoreGuestCart,
      fetchEcomStoreOrderAdminCart
    });
    fetchStoreCart(pkEcomStore, {
      ecomCustomerAddress,
      shippingOptions,
      includeShipping: true
    });
  };

  return {
    shippingAddress,
    shippingLoading: debouncedState.loading,
    debouncedState: debouncedState.value,
    onChangeShippingAddress,
    onShippingCallback,
    onChangeShippingCosts
  };
};

export { useCartShippingController };
